/**
 * An artificial delay in front of a request, for looking at loading states.
 *
 * The surgeon dashboard's skeletons, progress lines and `reloading` dimming all live in the window
 * between a request leaving and its response landing, and against a local API that window is a few
 * tens of milliseconds — too short to look at, let alone screenshot. Setting
 * `VITE_APP_FAKE_API_DELAY_MS` stretches it.
 *
 * Two gates, both of which have to open. `import.meta.env.DEV` is a compile-time constant, so a
 * production build folds the branch away and drops this whole module; the env var is unset in
 * `.env.base`, so a dev server is unaffected until someone opts in. Nothing here belongs in a
 * deployed build, and nothing here can reach one.
 *
 * Accepts a plain number of milliseconds (`1500`) or a range (`800-2500`), which picks a fresh value
 * per call — a range is what makes the four perfusion sections settle one at a time instead of in
 * lockstep, which is the arrangement their per-section skeletons and announcements are written for.
 */
const parseDelaySetting = (setting: string | undefined): (() => number) | undefined => {
  if (!setting) {
    return undefined;
  }

  const [from, to] = setting.split('-').map(part => Number(part.trim()));
  if (!Number.isFinite(from) || from < 0) {
    // eslint-disable-next-line no-console
    console.warn('[dev-delay] ignoring unreadable VITE_APP_FAKE_API_DELAY_MS', setting);
    return undefined;
  }
  if (to === undefined) {
    return () => from;
  }
  if (!Number.isFinite(to) || to < from) {
    // eslint-disable-next-line no-console
    console.warn('[dev-delay] ignoring unreadable VITE_APP_FAKE_API_DELAY_MS range', setting);
    return () => from;
  }
  return () => from + Math.random() * (to - from);
};

const nextDelay = import.meta.env.DEV ?
  parseDelaySetting(import.meta.env.VITE_APP_FAKE_API_DELAY_MS) :
  undefined;

/**
 * Wait, if a delay is configured, and return immediately otherwise.
 *
 * `await devDelay()` in front of the request rather than after the response, so the shape of the
 * waterfall the code produces is unchanged: what would have been concurrent still is, and what
 * waits on something else still waits.
 */
export const devDelay = async (): Promise<void> => {
  if (!nextDelay) {
    return;
  }
  await new Promise<void>(resolve => setTimeout(resolve, nextDelay()));
};
